import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { SEO } from '../../utils/seo';

export default function AboutPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = 'https://jl-security.co.uk';

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "JL Security",
      "description": "A security, risk and compliance consultancy led by John Lewis with over 20 years of experience supporting Defence, NHS, Local Authorities and Private Sector clients across North Wales and the UK.",
      "url": `${siteUrl}/about`,
      "foundingDate": "2003",
      "founder": {
        "@type": "Person",
        "name": "John Lewis",
        "jobTitle": "Managing Director & Principal Security, Risk & Compliance Consultant"
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "North Wales",
        "addressRegion": "Wales",
        "addressCountry": "GB"
      },
      "areaServed": [
        { "@type": "Place", "name": "North Wales" },
        { "@type": "Place", "name": "North West England" },
        { "@type": "Place", "name": "United Kingdom" }
      ],
      "knowsAbout": [
        "Security Consultancy",
        "Fire Safety",
        "Risk Assessment",
        "Compliance Solutions",
        "Emergency Response",
        "Martyn\\'s Law",
        "Training Services"
      ]
    }
  };

  const capabilities = [
    {
      icon: 'ri-shield-check-line',
      title: 'Consultancy & Compliance',
      description: 'Risk assessments, Martyn\'s Law planning, gap analysis, policies, incident planning, safeguarding, and ISO-aligned governance'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Training & Preparedness',
      description: 'First aid, emergency response, safety & compliance, wellbeing & vulnerability, security & response training delivered onsite, classroom & online'
    },
    {
      icon: 'ri-tools-line',
      title: 'Technical & System Solutions',
      description: 'CCTV, fire alarms, access control, electrical testing, solar PV, EV charging, cable laying via accredited specialist partners'
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Security & Event Operations',
      description: 'Close protection, corporate and private event security, backfill support for security providers, and site security operations'
    }
  ];

  const sectors = [
    { name: 'Holiday Parks & Tourism', icon: 'ri-hotel-line' },
    { name: 'Venues & Hospitality', icon: 'ri-restaurant-line' },
    { name: 'Farms & Rural Estates', icon: 'ri-plant-line' },
    { name: 'Retail & Leisure', icon: 'ri-shopping-bag-line' },
    { name: 'Schools & Education', icon: 'ri-school-line' },
    { name: 'Public Buildings', icon: 'ri-government-line' }
  ];

  const stats = [
    { number: '20+', label: 'Years Experience' },
    { number: '500+', label: 'Clients Served' },
    { number: '1000+', label: 'Projects Delivered' },
    { number: '24/7', label: 'Support Available' }
  ];

  const values = [
    {
      icon: 'ri-shield-star-line',
      title: 'Expertise You Can Trust',
      description: 'Former British Army with 20+ years in security operations, risk management and leadership. Proven track record supporting Defence, NHS, Local Authorities and Private Sector clients'
    },
    {
      icon: 'ri-team-line',
      title: 'One Partner Approach',
      description: 'One coordinated partner instead of multiple contractors — from consultancy through to training and implementation'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Practical Solutions',
      description: 'Proportionate and practical — not over-engineered. Solutions that work in real-world conditions'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Local & National',
      description: 'North Wales based with UK capability. Onsite delivery and online learning for national reach'
    }
  ];

  const whyChooseUs = [
    'One partner instead of multiple contractors',
    'Real operational and compliance experience',
    'Training aligned to identified risks',
    'Technical work delivered by accredited specialists',
    'Practical and proportionate — not over-engineered',
    'North Wales based with UK capability',
    'Online learning — national reach'
  ];

  const qualifications = [
    {
      title: 'First Aid at Work Instructor',
      organization: 'TQUK Endorsed',
      icon: 'ri-heart-pulse-line'
    },
    {
      title: 'Paediatric First Aid Instructor',
      organization: 'Accredited',
      icon: 'ri-parent-line'
    },
    {
      title: 'AED Instructor',
      organization: 'Certified',
      icon: 'ri-heart-add-line'
    },
    {
      title: 'Security Management Diploma',
      organization: 'Professional Qualification',
      icon: 'ri-shield-star-line'
    },
    {
      title: 'Leadership & Management',
      organization: 'Certified',
      icon: 'ri-team-line'
    },
    {
      title: 'Commercial Management',
      organization: 'Qualified',
      icon: 'ri-briefcase-line'
    },
    {
      title: 'GDPR & Workplace Confidentiality',
      organization: 'Certified',
      icon: 'ri-lock-line'
    },
    {
      title: 'Understanding Violence & Conflict Management',
      organization: 'Certified',
      icon: 'ri-shield-user-line'
    },
    {
      title: 'Close Protection Wing',
      organization: 'Specialist Modules',
      icon: 'ri-user-shield-line'
    }
  ];

  return (
    <>
      <SEO
        title="About JL Security | Security Risk Compliance Consultancy North Wales"
        description="JL Security is led by John Lewis, a security, risk and compliance professional with over 20 years experience helping organisations across North Wales reduce risk, meet regulatory obligations and translate compliance requirements into practical solutions."
        keywords="JL Security about, security consultancy North Wales, risk management consultant, compliance solutions, John Lewis security"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar isScrolled={isScrolled} />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">About JL Security</h6>
              <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight font-poppins">
                Security, Risk & Compliance Consultancy
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed font-inter">
                JL Security helps organisations reduce risk, meet compliance obligations and improve safety through practical consultancy, accredited training, technical systems and security operations.
              </p>
            </div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-6">
                <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider font-inter">Who We Are</h6>
                <h2 className="text-4xl font-bold text-gray-900 leading-tight font-poppins">
                  John Lewis – Founder & Lead Consultant
                </h2>
                <p className="text-base text-gray-600 leading-relaxed font-inter">
                  JL Security is led by John Lewis, a security, risk and compliance professional with over 20 years' experience supporting public-facing, regulated and high-risk environments across the defence, nuclear, NHS, public sector and private sectors.
                </p>
                <p className="text-base text-gray-600 leading-relaxed font-inter">
                  John specialises in helping organisations understand risk, meet regulatory obligations and translate compliance requirements into practical, workable solutions. His experience spans security management, emergency planning, governance, and the design and delivery of training aligned to real operational risk.
                </p>
                <p className="text-base text-gray-600 leading-relaxed font-inter">
                  Through JL Security, John provides end-to-end support — from assessment and planning through to staff training, technical coordination and ongoing compliance support — ensuring organisations are prepared, resilient and inspection-ready without unnecessary complexity.
                </p>
              </div>

              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl h-[600px] w-full">
                  <img
                    src="https://static.readdy.ai/image/74d1e4e79521c370342183571976e552/74a3525ec8c6a0406e0271e8a4d240bd.png"
                    alt="John Lewis - JL Security Founder & Lead Consultant"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-red-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl font-bold text-white mb-2 font-poppins">{stat.number}</div>
                  <div className="text-sm text-red-100 font-inter">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Capabilities */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">Our Service Divisions</h6>
              <h2 className="text-4xl font-bold text-gray-900 mb-6 font-poppins">
                End-to-End Safety & Compliance Solutions
              </h2>
              <p className="text-base text-gray-600 font-inter">
                From initial risk assessment to implementation and ongoing support, we provide comprehensive services to keep your operations safe and compliant.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {capabilities.map((capability, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${capability.icon} text-3xl text-red-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">{capability.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed font-inter">{capability.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Founder Credentials Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-10 border border-gray-200 shadow-lg">
                <div className="text-center mb-8">
                  <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">Founder Credentials</h6>
                  <h2 className="text-3xl font-bold text-gray-900 font-poppins mb-2">
                    John Lewis
                  </h2>
                  <p className="text-base text-gray-600 font-semibold font-inter mb-6">
                    Managing Director & Principal Security, Risk & Compliance Consultant
                  </p>
                </div>
                <p className="text-base text-gray-700 leading-relaxed font-inter text-center">
                  Qualified in enforcement, conflict management, fire safety and frontline operational compliance, with real-world experience across defence, NHS, nuclear, public sector and private operations. Certified through High Court Enforcement Group (Enforcement & Conflict Management), TQUK Fire Safety Principles, and multiple operational leadership and safety training programmes — delivering practical, proportionate and compliant solutions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Experience & Background Section */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl h-[600px] w-full">
                  <img
                    src="https://static.readdy.ai/image/74d1e4e79521c370342183571976e552/b212b73baaef160eb5775a1b8f1d43ef.jpeg"
                    alt="JL Security - Military Experience and Leadership"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="space-y-6">
                <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider font-inter">Experience & Background</h6>
                <h2 className="text-4xl font-bold text-gray-900 leading-tight font-poppins">
                  Built on Military Experience & 20+ Years of Leadership
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-shield-star-line text-sm text-red-600"></i>
                    </div>
                    <p className="text-base text-gray-700 font-inter">
                      <strong>Former British Army</strong> — experienced in high-pressure and high-risk environments
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-time-line text-sm text-red-600"></i>
                    </div>
                    <p className="text-base text-gray-700 font-inter">
                      <strong>20+ years</strong> in security operations, risk management and leadership
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-building-line text-sm text-red-600"></i>
                    </div>
                    <p className="text-base text-gray-700 font-inter">
                      <strong>Experience supporting</strong> Defence, NHS, Local Authorities and Private Sector clients
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-team-line text-sm text-red-600"></i>
                    </div>
                    <p className="text-base text-gray-700 font-inter">
                      <strong>Proven track record</strong> building teams, improving safety culture and managing incidents
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-8 mt-8 shadow-lg border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 font-poppins">Who We Support</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {sectors.map((sector, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className={`${sector.icon} text-lg text-red-600`}></i>
                        </div>
                        <span className="text-sm text-gray-700 font-medium font-inter">{sector.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">Our Values</h6>
              <h2 className="text-4xl font-bold text-gray-900 mb-6 font-poppins">
                What Sets Us Apart
              </h2>
              <p className="text-base text-gray-600 font-inter">
                We're not just another security company. Our approach is built on real operational experience and a commitment to practical, effective solutions.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className={`${value.icon} text-4xl text-red-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 font-poppins">{value.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed font-inter">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-6">
                <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider font-inter">Why Choose Us</h6>
                <h2 className="text-4xl font-bold text-gray-900 leading-tight font-poppins">
                  Trusted Partners, Not Just Suppliers
                </h2>
                <p className="text-base text-gray-600 leading-relaxed font-inter">
                  We work alongside insured specialist partners to deliver comprehensive solutions that meet the highest standards of quality and compliance. Our integrated approach means you have one trusted partner managing your entire safety and compliance journey.
                </p>

                <div className="space-y-4 pt-4">
                  {whyChooseUs.map((reason, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-sm text-red-600"></i>
                      </div>
                      <span className="text-base text-gray-700 font-inter">{reason}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl h-[600px] w-full">
                  <img
                    src="https://static.readdy.ai/image/74d1e4e79521c370342183571976e552/c89a1b1a0d7fd69a6dc3bb604fd816b8.png"
                    alt="security services in Liverpool"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Partnership Approach */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">Our Approach</h6>
                <h2 className="text-4xl font-bold text-gray-900 leading-tight font-poppins">
                  Translating Compliance Into Practical Action
                </h2>
                <p className="text-base text-gray-600 leading-relaxed font-inter">
                  We work with organisations to identify risk, understand their obligations and put proportionate controls in place — without unnecessary complexity or over-engineering.
                </p>
                <p className="text-base text-gray-600 leading-relaxed font-inter">
                  Our support covers assessment and planning, staff training, technical coordination through accredited partners, and ongoing compliance support to ensure organisations remain prepared and inspection-ready.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-gray-50 rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-search-eye-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 font-poppins">Assessment</h3>
                  <p className="text-sm text-gray-600 font-inter">
                    Comprehensive risk identification, site evaluation and compliance planning tailored to sector needs and real operational environments.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-tools-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 font-poppins">Implementation</h3>
                  <p className="text-sm text-gray-600 font-inter">
                    Professional installation, systems integration, and training through accredited and insured specialist partners.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-customer-service-2-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 font-poppins">Support</h3>
                  <p className="text-sm text-gray-600 font-inter">
                    Ongoing maintenance, monitoring, documentation, and compliance support when required — not only at the point of installation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Qualifications & Credentials */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">Professional Credentials</h6>
              <h2 className="text-4xl font-bold text-gray-900 mb-6 font-poppins">
                Qualifications & Expertise
              </h2>
              <p className="text-base text-gray-600 max-w-3xl mx-auto font-inter">
                Qualified in enforcement, conflict management, fire safety and frontline operational compliance, with real-world experience across defence, NHS, nuclear, public sector and private operations.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {qualifications.map((qual, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-gray-100"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${qual.icon} text-xl text-red-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900 mb-1 font-poppins">{qual.title}</h3>
                      <p className="text-sm text-gray-600 font-inter">{qual.organization}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Training Network Statement */}
            <div className="bg-red-50 rounded-2xl p-8 border border-red-100">
              <div className="text-center max-w-3xl mx-auto">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Our Training Network</h3>
                <p className="text-base text-gray-700 leading-relaxed font-inter">
                  We deliver courses through a combination of accredited instructors, awarding-body partners and specialist trainers, enabling flexible delivery nationwide — online, workplace-based or on-site — without clients being restricted to a single venue or provider.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-red-600 to-red-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6 font-poppins">
              Ready to Enhance Your Security & Compliance?
            </h2>
            <p className="text-lg text-red-100 mb-8 font-inter">
              Let's discuss how we can support your safety and compliance journey with practical, proven solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-inter"
              >
                Get in Touch
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a
                href="tel:+447834561015"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white hover:text-red-600 transition-all duration-300 whitespace-nowrap cursor-pointer font-inter"
              >
                <i className="ri-phone-line mr-2"></i>
                +44 7834 561015
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
